import { ArrowLeft, Award, CheckCircle2, LockKeyhole, Mail, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSeo } from '@/lib/seoHead';

const POLICY_URL = 'https://cnapractice.com/privacy';
const PRIVACY_EMAIL = 'admin@cnapractice.com';

const sections = [
  { id: 'information', label: 'Information we collect' },
  { id: 'use', label: 'How we use information' },
  { id: 'sharing', label: 'Service providers and sharing' },
  { id: 'choices', label: 'Your choices and rights' },
  { id: 'account-deletion', label: 'Account deletion' },
  { id: 'security', label: 'Security and retention' },
  { id: 'children', label: 'Children’s privacy' },
  { id: 'contact', label: 'Contact us' },
];

export function PrivacyPolicy() {
  useSeo({
    title: 'Privacy Policy · Huduma CNA Exam',
    description: 'How Huduma CNA Exam collects, uses, protects, and deletes personal information.',
    canonical: POLICY_URL,
    ogType: 'website',
  });

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900 selection:bg-emerald-100">
      <header className="sticky top-0 z-30 border-b border-stone-200/80 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" className="flex items-center gap-3 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-700 text-white shadow-sm">
              <Award className="h-6 w-6" aria-hidden="true" />
            </span>
            <span>
              <span className="block text-sm font-bold tracking-tight">Huduma CNA Exam</span>
              <span className="block text-[11px] font-medium text-stone-500">Privacy Center</span>
            </span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-stone-600 hover:bg-stone-100 hover:text-stone-900">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Home
          </Link>
        </div>
      </header>

      <main>
        <section className="overflow-hidden border-b border-stone-200 bg-white">
          <div className="relative mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
            <div className="absolute -right-24 -top-28 h-72 w-72 rounded-full bg-emerald-100/70 blur-3xl" aria-hidden="true" />
            <div className="relative max-w-3xl">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-emerald-800">
                <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                Your privacy matters
              </div>
              <h1 className="text-4xl font-black tracking-tight text-stone-950 sm:text-5xl">Privacy Policy</h1>
              <p className="mt-5 max-w-2xl text-base leading-7 text-stone-600 sm:text-lg">
                This policy explains how Huduma CNA Exam handles information when you use our Android app, website, exam-preparation services, and support channels.
              </p>
              <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-stone-500">
                <span><strong className="text-stone-700">Effective:</strong> August 10, 2026</span>
                <span><strong className="text-stone-700">Last updated:</strong> August 15, 2026</span>
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto grid max-w-6xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[250px_minmax(0,1fr)] lg:py-14">
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <nav aria-label="Privacy policy sections" className="rounded-2xl border border-stone-200 bg-white p-3 shadow-sm">
              <p className="px-3 pb-2 pt-1 text-xs font-bold uppercase tracking-[0.14em] text-stone-400">On this page</p>
              {sections.map((section) => (
                <a key={section.id} href={`#${section.id}`} className="block rounded-lg px-3 py-2 text-sm font-medium text-stone-600 hover:bg-emerald-50 hover:text-emerald-800">
                  {section.label}
                </a>
              ))}
            </nav>
          </aside>

          <article className="min-w-0 rounded-3xl border border-stone-200 bg-white px-5 py-8 shadow-sm sm:px-9 sm:py-10">
            <div className="mb-10 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
              <div className="flex gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" aria-hidden="true" />
                <p className="text-sm leading-6 text-emerald-950">
                  We do not sell your personal information. We use it to provide and improve your study experience, protect accounts, process subscriptions, and support you.
                </p>
              </div>
            </div>

            <PolicySection id="information" title="1. Information we collect">
              <p>Depending on how you use the service, we collect the following categories of information:</p>
              <ul>
                <li><strong>Account and profile information:</strong> name, email address, phone number, testing state, planned exam date, email-verification status, and account identifiers.</li>
                <li><strong>Authentication information:</strong> credentials handled by Firebase Authentication or, when you choose Google sign-in, the basic identity information Google shares with us. We do not receive your Google password.</li>
                <li><strong>Study activity:</strong> questions viewed, answer choices, scores, exam sessions, bookmarks, progress, categories practiced, and related timestamps.</li>
                <li><strong>Subscription information:</strong> plan and subscription status, transaction status, and Stripe customer or subscription identifiers. Payment card and bank details are collected and processed by Stripe; we do not store full card or bank-account numbers.</li>
                <li><strong>Device and diagnostics:</strong> app version, device and operating-system information, network state, performance data, usage events, and crash diagnostics.</li>
                <li><strong>Communications:</strong> information you include when contacting support or requesting account deletion.</li>
              </ul>
              <p>The question read-aloud feature uses your device’s text-to-speech service. Huduma CNA Exam does not record your voice and does not require microphone access for this feature.</p>
            </PolicySection>

            <PolicySection id="use" title="2. How we use information">
              <ul>
                <li>Create, authenticate, secure, and maintain your account.</li>
                <li>Deliver practice questions, save progress, calculate results, and personalize study insights.</li>
                <li>Process subscriptions and keep your access status current.</li>
                <li>Send verification, password-reset, account, billing, and support communications.</li>
                <li>Detect abuse, troubleshoot failures, monitor reliability, and improve the app.</li>
                <li>Meet legal, accounting, security, and regulatory obligations.</li>
              </ul>
            </PolicySection>

            <PolicySection id="sharing" title="3. Service providers and sharing">
              <p>We disclose information only as needed to operate the service, complete a request, protect users, or comply with law. Our principal service providers include:</p>
              <ul>
                <li><strong>Google and Firebase</strong> for authentication, cloud data storage, server functions, hosting, analytics, performance monitoring, and crash reporting.</li>
                <li><strong>Stripe</strong> for secure in-app payment and subscription processing.</li>
                <li><strong>Namecheap Private Email</strong> for transactional and support email delivery.</li>
              </ul>
              <p>These providers process information under their own privacy terms and security controls. We may also disclose information if required by law, to investigate fraud or security threats, or in connection with a business reorganization where permitted by law.</p>
              <p><strong>We do not sell or rent personal information, and we do not share it for cross-context behavioral advertising.</strong></p>
            </PolicySection>

            <PolicySection id="choices" title="4. Your choices and rights">
              <p>You may review and update profile details from the Account screen. You may opt out of optional device-level analytics where your operating system provides that control. Depending on where you live, you may also request access, correction, deletion, restriction, or a portable copy of personal information.</p>
              <p>To exercise a privacy right, email <EmailLink /> from the address associated with your account. We may need to verify your identity before completing the request.</p>
            </PolicySection>

            <PolicySection id="account-deletion" title="5. Account deletion">
              <div className="rounded-2xl border border-stone-200 bg-stone-50 p-5">
                <p className="font-semibold text-stone-900">To delete your Huduma CNA Exam account and associated data:</p>
                <ol>
                  <li>In the Android app, open <strong>Account</strong>, choose <strong>Delete account</strong>, and confirm your identity. The app confirms when deletion is complete.</li>
                  <li>If you cannot access the app, email <EmailLink subject="Delete my Huduma CNA Exam account" /> from the address registered to your account with the subject <strong>Delete my Huduma CNA Exam account</strong>.</li>
                  <li>We verify email requests and normally complete them within 30 days.</li>
                </ol>
              </div>
              <p>Deletion removes or anonymizes your profile, saved study progress, exam history, bookmarks, and authentication account. A limited pseudonymous deletion audit may be retained for up to 90 days for security and operational integrity. Some payment, tax, dispute, or other records may be retained where legally required, and Stripe may retain transaction records under its own obligations. Deleting the app from your device does not delete your account.</p>
            </PolicySection>

            <PolicySection id="security" title="6. Security and retention">
              <p>We use reasonable administrative, technical, and organizational safeguards, including encrypted network connections, managed authentication, access controls, and restricted production access. No internet service can guarantee absolute security.</p>
              <p>We keep information while your account is active and as reasonably necessary to provide the service. When you delete your account, covered data is deleted or anonymized subject to limited legal, security, backup, and financial-record retention needs. Backup copies may remain for a limited period before routine deletion.</p>
            </PolicySection>

            <PolicySection id="children" title="7. Children’s privacy">
              <p>Huduma CNA Exam is intended for people preparing for professional nurse aide examinations and is not directed to children under 13. We do not knowingly collect personal information from children under 13. If you believe a child has provided information, contact us so we can investigate and delete it.</p>
            </PolicySection>

            <PolicySection id="changes" title="8. Changes to this policy">
              <p>We may update this policy as the service or legal requirements change. We will post the revised version at this URL and update the “Last updated” date. If a change materially affects your rights, we will provide additional notice where appropriate.</p>
            </PolicySection>

            <PolicySection id="contact" title="9. Contact us">
              <p>Huduma CNA Exam is operated by Caregiver Training and Huduma Center LLC. For privacy questions, rights requests, or account deletion:</p>
              <a href={`mailto:${PRIVACY_EMAIL}`} className="mt-3 inline-flex items-center gap-3 rounded-2xl border border-stone-200 bg-stone-50 px-4 py-3 font-semibold text-emerald-800 hover:border-emerald-300 hover:bg-emerald-50">
                <Mail className="h-5 w-5" aria-hidden="true" />
                {PRIVACY_EMAIL}
              </a>
            </PolicySection>

            <div className="mt-10 flex items-start gap-3 border-t border-stone-200 pt-7 text-xs leading-5 text-stone-500">
              <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <p>Canonical policy URL: <a className="font-semibold text-emerald-800 underline-offset-2 hover:underline" href={POLICY_URL}>{POLICY_URL}</a></p>
            </div>
          </article>
        </div>
      </main>

      <footer className="border-t border-stone-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 py-6 text-xs text-stone-500 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <span>© {new Date().getFullYear()} Huduma CNA Exam</span>
          <span>Professional CNA exam preparation</span>
        </div>
      </footer>
    </div>
  );
}

function PolicySection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24 border-t border-stone-200 py-9 first:border-t-0 first:pt-0">
      <h2 className="text-xl font-extrabold tracking-tight text-stone-950 sm:text-2xl">{title}</h2>
      <div className="mt-4 space-y-4 text-[15px] leading-7 text-stone-600 [&_li]:pl-1 [&_ol]:ml-5 [&_ol]:list-decimal [&_ol]:space-y-2 [&_strong]:font-semibold [&_strong]:text-stone-800 [&_ul]:ml-5 [&_ul]:list-disc [&_ul]:space-y-2">
        {children}
      </div>
    </section>
  );
}

function EmailLink({ subject }: { subject?: string }) {
  const suffix = subject ? `?subject=${encodeURIComponent(subject)}` : '';
  return <a className="font-semibold text-emerald-800 underline underline-offset-2" href={`mailto:${PRIVACY_EMAIL}${suffix}`}>{PRIVACY_EMAIL}</a>;
}
